import { DatePicker, Drawer, message, Select } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import dayjs, { Dayjs } from "dayjs";
import { createExpense, fetchExpensesByEmployeeId, fetchUniquePaymentMethods, updateExpense } from "../../../core/data/redux/payroll/Accounting/expenses/expenseActions";
import { CloseCircleOutlined } from "@ant-design/icons";

const ExpenseModal: React.FC<any> = ({ modalType, handleCancel, isModalVisible, selectedRowData, setTableKey, employeeId }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: any) => state.auth);
    const { loadingStates, uniquePaymentMethods } = useSelector((state: any) => state.accounting.expenses);
    const isLoading = modalType === "edit" ? loadingStates.update : loadingStates.create;

    const getModalContainer = () => document.getElementById('modal-datepicker') || document.body;

    const [formData, setFormData] = useState<any>({
        expense_name: "",
        expense_date: null,
        amount: "",
        payment_method: null,
        status: "active",
        createdBy: user?.UserId || 1,
        document: null,
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    useEffect(() => {
        if (modalType === "edit" && selectedRowData) {
            setFormData({
                expense_name: selectedRowData.expense_name || "",
                expense_date: selectedRowData.expense_date ? dayjs(selectedRowData.expense_date) : null,
                amount: selectedRowData.amount || "",
                payment_method: selectedRowData.payment_method || "",
                status: selectedRowData.status || "active",
                createdBy: user?.UserId || 1,
                document: selectedRowData.doc_path ? { name: selectedRowData.doc_path.split("/").pop() } : null,
            });
            setErrors({});
        } else {
            resetForm();
        }
    }, [modalType, selectedRowData]);

    useEffect(() => {
        dispatch(fetchUniquePaymentMethods());
    }, [dispatch])


    const resetForm = () => {
        setFormData({
            expense_name: "",
            expense_date: null,
            amount: "",
            payment_method: null,
            status: "active",
            createdBy: user?.UserId || 1,
            document: null,
        });
        setErrors({});
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files[0]) {
            setFormData((prev: any) => ({
                ...prev,
                document: files[0], 
            }));
        } else {
            setFormData((prev: any) => ({
                ...prev,
                document: null,
            }));
        }
    };


    const handleChange = (field: string, value: string | Dayjs | null) => {
        setFormData((prev: any) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: "" }));
        if (field === "payment_method" && typeof value === "string") {
            value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
        }
    };

    const validateForm = () => {
        let valid = true;
        const newErrors: Record<string, string> = {};

        if (!formData?.expense_name || !formData.expense_name.trim()) {
            newErrors.expense_name = "Reimbursement name is required.";
            valid = false;
        }
        if (!formData.expense_date) {
            newErrors.expense_date = "Date is required.";
            valid = false;
        }
        if (!formData.amount || isNaN(Number(formData.amount))) {
            newErrors.amount = "Valid amount is required.";
            valid = false;
        }
        if (!formData.payment_method || !formData.payment_method.trim()) {
            newErrors.payment_method = "Payment method is required.";
            valid = false;
        }
        // if (!formData.status) {
        //     newErrors.status = "Status is required.";
        //     valid = false;
        // }

        setErrors(newErrors);
        return valid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        const basePayload = {
            ...formData,
            requestStatus: "Pending",
            employeeId,
            expense_date: formData.expense_date?.format("YYYY-MM-DD") || "",
        };
        if (!(formData.document instanceof File)) {
            delete basePayload.document;
        }
        try {
            if (modalType === "edit") {
                const payloadWithModifiedBy = {
                    ...basePayload,
                    modifiedBy: user?.id || 1,
                };
                await dispatch(updateExpense({ id: selectedRowData.expense_id, data: payloadWithModifiedBy })).unwrap();
                await dispatch(fetchExpensesByEmployeeId(employeeId))
                await dispatch(fetchUniquePaymentMethods());
                setTableKey((prevKey: any) => prevKey + 1);
                message.success("Reimbursement updated successfully");
            } else {
                await dispatch(createExpense(basePayload)).unwrap();
                await dispatch(fetchExpensesByEmployeeId(employeeId))
                await dispatch(fetchUniquePaymentMethods());
                setTableKey((prevKey: any) => prevKey + 1);
                message.success("Reimbursement created successfully");
            }
            handleCancel();
            resetForm();
        } catch {
            message.error("Something went wrong. Please try again.");
        }
    };
    return (
        <Drawer
            title={modalType === "edit" ? "Edit Reimbursement" : "Add Reimbursement"}
            placement="right"
            width={400}
            onClose={() => {
                handleCancel();
                resetForm();
            }}
            visible={isModalVisible}
            style={{ marginTop: 50, paddingBottom: 50 }}
        >
            <form>
                <div className="modal-body pb-0">
                    <div className="mb-3">
                        <label className="form-label">Reimbursement Name <span className="text-danger"> *</span></label>
                        <input
                            type="text"
                            className={`form-control ${errors.expense_name ? "is-invalid" : ""}`}
                            value={formData.expense_name}
                            onChange={(e) => handleChange("expense_name", e.target.value)}
                        />
                        {errors.expense_name && <div className="text-danger">{errors.expense_name}</div>}
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Date <span className="text-danger"> *</span></label>
                        <div className="input-icon-end position-relative">
                            <DatePicker
                                className={`form-control datetimepicker ${errors.expense_date ? "is-invalid" : ""}`}
                                format="DD-MM-YYYY"
                                getPopupContainer={getModalContainer}
                                placeholder="DD-MM-YYYY"
                                value={formData.expense_date}
                                onChange={(date) => handleChange("expense_date", date)}
                            />
                            <span className="input-icon-addon">
                                <i className="ti ti-calendar text-gray-7" />
                            </span>
                        </div>
                        {errors.expense_date && <div className="text-danger">{errors.expense_date}</div>}
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Amount <span className="text-danger"> *</span></label>
                        <input
                            type="text" // Change type to text to better handle non-numeric characters
                            className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                            value={formData.amount}
                            onChange={(e) => handleChange("amount", e.target.value.replace(/[^0-9.]/g, ""))} // Allow only numbers and dot
                        />
                        {errors.amount && <div className="text-danger">{errors.amount}</div>}
                    </div>


                    <div className="mb-3">
                        <label className="form-label">Payment Method <span className="text-danger"> *</span></label>
                        <Select
                            mode="tags"
                            style={{ width: '100%' }}
                            placeholder="Select or enter a payment method"
                            value={formData.payment_method}
                            onChange={(value) => handleChange("payment_method", typeof value === "string" ? value : value[0])}
                            className={errors.payment_method ? "is-invalid-select" : ""}
                            tokenSeparators={[',']}
                            tagRender={(props) => (
                                <div className="ant-select-tag">
                                    <span>{props.label}</span>
                                    <CloseCircleOutlined
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            props.onClose();
                                        }}
                                        style={{ color: 'red', marginLeft: '8px' }}
                                    />
                                </div>
                            )}
                        >
                            {uniquePaymentMethods?.map((method: string) => (
                                <Select.Option key={method} value={method}>
                                    <span className="text-primary">{method}</span>
                                </Select.Option>
                            ))}
                        </Select>
                        {errors.payment_method && <div className="text-danger">{errors.payment_method}</div>}
                    </div>



                    {/* <div className="mb-3">
                        <label className="form-label">Status</label>
                        <Select
                            value={formData.status}
                            onChange={(value) => handleChange("status", value)}
                            className={`w-100 ${errors.status ? "is-invalid-select" : ""}`}
                        >
                            {status.map((option) => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                        {errors.status && <div className="text-danger mt-1">{errors.status}</div>}
                    </div> */}
                </div>
                <div className="mb-3">
                    {/* <label className="form-label">Upload File</label> */}
                    <div className="file-upload position-relative btn btn-sm btn-primary px-3 mb-2">
                        <input
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={handleFileChange}
                            style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }}
                        />
                        <span className="fs-14 text-center mb-2">Choose File</span>
                    </div>
                    {/* ✅ File name preview */}
                    {formData.document && (
                        <div className="mt-2">
                            <strong>Selected File:</strong> {formData.document.name}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn btn-white border me-2"
                        onClick={() => {
                            handleCancel();
                            resetForm();
                        }}
                    >
                        Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={isLoading}>
                        {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
                        {modalType === "edit" ? "Update Reimbursement" : "Add Reimbursement"}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default ExpenseModal;
